import React, { useContext, useMemo, useState } from 'react';
import { ListingTable as Listings } from 'components';
import styled from 'styled-components';
import {
  Icon,
  InputSearch,
  message,
  Switch,
  Text,
  Tooltip,
  Button,
} from '@bb/elements-web';
import errorReporter from '@app/errorReporter';
import { setAnsweringDefaultEnabled } from 'api/AnsweringSubscriptionController';
import { AuthContext, ListingLabelContext } from 'contexts';
import { ListingAvailability } from '@bb/common';
import { useHistory } from 'react-router-dom';
import ManageListingsContext from './ManageListingsContext';
import AnsweringServiceContext from '../AnsweringServiceContext';
import { TOGGLE_CARD_HEIGHT } from './ManageListingsLayout';

/**
 * The table showing all of the agent or brokerage's current listings.
 * Shares the same ListingTable component we use on other pages.
 * This one meant to be shown in the ManageListings page of the Answering Service flow.
 */
const ListingTable: React.FC = () => {
  // Grab various values from the contexts.
  const {
    containerRef,
    footerRef,
    searchResults: { listings, count },
    loading,
    queryParameter,
    totalCount,
    enabledCount,
    setQueryParameter,
    sort,
    openModal,
  } = useContext(ManageListingsContext);
  const {
    answeringSubscription,
    defaultEnabled,
    setDefaultEnabled,
    viewingLevel,
  } = useContext(AnsweringServiceContext);
  const { auth } = useContext(AuthContext);
  const { getAvailability } = useContext(ListingLabelContext);
  const history = useHistory();

  // Open & close the modals.
  const handleEnableAll = () => {
    openModal('enableListings');
  };
  const handleDisableAll = () => {
    openModal('disableListings');
  };
  /** Whether we are loading the default preference toggle. */
  const [toggleLoading, setToggleLoading] = useState(false);

  /* Determine how much space is available in the viewport above
   * the table so we dynamically set the table height to only show
   * enough rows at a time as will fit on the rest of the user's page.
   */
  const TABLE_HEADER_HEIGHT = 47; // antd value
  const SEARCH_BAR_HEIGHT = 60; // antd value for input size === 'large' + margin
  const PAGINATION_HEIGHT = 64; // 32px + 32px margin
  const tableHeightOffset = useMemo(() => {
    const container = containerRef.current;
    const footer = footerRef.current;
    return (
      (container?.getBoundingClientRect().top ?? 0) +
      (footer?.getBoundingClientRect().height ?? 0) +
      TOGGLE_CARD_HEIGHT +
      TABLE_HEADER_HEIGHT +
      SEARCH_BAR_HEIGHT +
      PAGINATION_HEIGHT +
      2 // the height for the 2 divider line
    );
  }, [containerRef, footerRef]);

  /** Create the different options that will show up in the columns that have filters */
  const filterOptions = useMemo(() => {
    const APPT_DESK_AVAILABILITY_FILTER: ListingAvailability[] = [
      'Price Change',
      'Extended',
      'Deal Fell Through',
      'New',
    ];

    return {
      // Filters for the column showing if Answering Service is enabled
      useAppointmentDesk: [
        {
          text: 'Enabled',
          value: true,
        },
        {
          text: 'Disabled',
          value: false,
        },
      ],
      // Filters for the column showing the listing's availability
      availability: APPT_DESK_AVAILABILITY_FILTER.map(value => ({
        text: getAvailability(value, {
          condensed: true,
        }),
        value,
      })),
      // Filters for the column showing the listing's brokerage branch
      location: auth.org.locations
        ?.filter(
          (_, index) =>
            viewingLevel === 'org' || index === parseInt(auth.location, 10),
        )
        .map((location, index) => ({
          text: location.name,
          value: index,
        })),
    };
  }, [auth.location, auth.org.locations, getAvailability, viewingLevel]);

  /**
   * The callback function for when the user toggles to change their
   * default preference for new listings having Answering Service on.
   */
  const handleNewListingsToggleChange = async (newChecked: boolean) => {
    if (answeringSubscription) {
      setToggleLoading(true);
      try {
        await setAnsweringDefaultEnabled(answeringSubscription.id, newChecked);
        setDefaultEnabled(newChecked);
      } catch (e) {
        message.error('Error updating default concierge preference');
        errorReporter.error(e);
      } finally {
        setToggleLoading(false);
      }
    } else {
      setDefaultEnabled(newChecked);
    }
  };

  // Return the actual JSX to display the table
  return (
    <>
      {/** The search bar above the table. */}
      <StyledSearchBarWrapper>
        <InputSearch
          allowClear
          onChange={({ target: { value } }) =>
            setQueryParameter({
              ...queryParameter,
              searchTerm: value,
            })
          }
          loading={loading}
          size="large"
          placeholder="Search addresses, agents"
          dataCy="listing-table-search"
        />
      </StyledSearchBarWrapper>
      {/** The bar with toggles below the search bar. */}
      <StyledToggleRow>
        <div>
          Default preference for new listings:
          <StyledInlineSwitch
            checked={defaultEnabled}
            onChange={handleNewListingsToggleChange}
            loading={toggleLoading}
            checkedChildren={'On'}
            unCheckedChildren={'Off'}
            dataCy="listing-default-toggle"
          />
          <Tooltip title="Select your preferred default setting for newly added listings.">
            <StyledInfoIconWrapper>
              <Icon icon="infoCircleOutlined" size={24} />
            </StyledInfoIconWrapper>
          </Tooltip>
        </div>
        <StyledButtonGroup>
          <StyledListChild
            data-enabled-count={enabledCount}
            data-total-count={totalCount}
          >
            {`${enabledCount} of ${totalCount} active listings enabled |`}
          </StyledListChild>
          <Button
            type="primary"
            size="small"
            onClick={handleEnableAll}
            dataCy="enable-button"
            style={{
              fontWeight: '500',
              minWidth: 'auto',
              padding: '2px 16px',
              fontSize: '15px',
            }}
          >
            Enable All
          </Button>
          <Button
            type="secondary"
            size="small"
            onClick={handleDisableAll}
            dataCy="disable-button"
            style={{
              fontWeight: '500',
              minWidth: 'auto',
              padding: '2px 16px',
              fontSize: '15px',
            }}
          >
            Disable All
          </Button>
        </StyledButtonGroup>
      </StyledToggleRow>
      {/** The actual table itself with all the listings. */}
      <div key={tableHeightOffset}>
        <Listings
          listings={listings}
          loading={loading}
          setRedirect={address => history.replace(address)}
          heightOffset={tableHeightOffset}
          sort={sort}
          sortField={queryParameter.sortData.field}
          sortOrder={
            queryParameter.sortData.order as 'descend' | 'ascend' | null
          }
          showSorter
          isFromLiveConciergePage
          onChange={(_, filter) => {
            setQueryParameter(prev => ({
              ...prev,
              filter: {
                ...prev.filter,
                useAppointmentDesk: filter.useAppointmentDesk as boolean[],
                availability: filter.availability as string[],
                location: filter.location as string[],
              },
            }));
          }}
          pagination={{
            current: queryParameter.pageNumber + 1,
            pageSize: 20,
            total: count,
            onChange: (pageNumber: number) => {
              setQueryParameter(prev => ({
                ...prev,
                pageNumber: pageNumber - 1,
              }));
            },
            disabled: loading,
            position: ['bottomCenter'],
            showSizeChanger: false,
            // dynamically add data-cy to each pagination element
            itemRender: (page, type, element: any) => ({
              ...element,
              props: {
                ...element.props,
                'data-cy': `listing-pagination-${type}-${page}`,
              },
            }),
          }}
          filtersOption={filterOptions}
        />
      </div>
    </>
  );
};

export default ListingTable;

const StyledInlineSwitch = styled(Switch)`
  display: inline;
  & > div {
    display: inline;
  }
  margin-left: 8px;

  .anticon {
    color: white;
  }
`;

const StyledInfoIconWrapper = styled.span`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 8px;
  position: relative;
  top: 1px;
`;

const StyledListChild = styled(Text)`
  &::before {
    content: '• ';
    color: #52c41a;
  }
`;

const StyledSearchBarWrapper = styled.div`
  .ant-input-affix-wrapper {
    background-color: #ffffff;
    border: 1px solid #c5c6d0;
    border-radius: 6px;
  }
  .ant-input {
    background-color: #ffffff;
  }

  .icon-wrapper svg,
  .icon-wrapper svg path {
    fill: #c5c6d0;
  }

  margin: 20px;
  & > span {
    border-radius: 2px;
  }
`;

const StyledToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #ffffff;
  border-top: 1px solid #f0f2f5;
  border-bottom: 1px solid #f0f2f5;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
